import React, { useState, useEffect } from 'react';
import { apiGet } from '../utils/api';

interface ContestReportProps {
  contestId: string;
  isExportMode?: boolean;
}

const ContestReport: React.FC<ContestReportProps> = ({ contestId, isExportMode = false }) => {
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const USER_COL_WIDTH = 200;
  const POLL_COL_WIDTH = 35;
  const TOTAL_COL_WIDTH = 48;

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // В режиме экспорта используем публичный endpoint без аутентификации
        let report;
        if (isExportMode) {
          const response = await fetch(`/api/polls/results/report/public?contestId=${contestId}`);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          report = await response.json();
        } else {
          report = await apiGet(`/polls/results/report?contestId=${contestId}`);
        }
        
        setReportData(report);
      } catch (err: any) {
        console.error('Ошибка загрузки отчета:', err);
        setError(err.response?.data?.error || err.message || 'Ошибка загрузки отчета');
      } finally {
        setLoading(false);
      }
    };

    if (contestId) {
      fetchReport();
    }
  }, [contestId, isExportMode]);

  if (loading) {
    return (
      <div style={{ padding: '20px', background: 'white', minHeight: '100vh' }}>
        <div>Загрузка...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', background: 'white', minHeight: '100vh' }}>
        <div style={{ color: 'red' }}>{error}</div>
      </div>
    );
  }

  if (!reportData || reportData.users.length === 0) {
    return (
      <div style={{ padding: '20px', background: 'white', minHeight: '100vh' }}>
        <div>Нет данных для отображения</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0', margin: '0', background: 'white', minHeight: '100vh' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Color+Emoji&display=swap');
        /* Убираем все отступы по умолчанию */
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          overflow: visible !important;
        }
        /* Базовые стили для эмодзи */
        body, * {
          font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji', 'EmojiOne Color', 'Android Emoji', 'Twemoji Mozilla', sans-serif;
        }
        /* Стили для заголовков */
        h2 {
          font-family: 'Arial', 'Helvetica Neue', Helvetica, sans-serif !important;
          font-variant-numeric: normal !important;
          font-feature-settings: normal !important;
          font-variant-ligatures: normal !important;
          font-kerning: normal !important;
          letter-spacing: normal !important;
          text-rendering: auto !important;
        }
        /* Стили для текста в ячейках таблицы */
        td {
          font-family: 'Arial', 'Helvetica Neue', Helvetica, sans-serif;
        }
        /* Эмодзи должны использовать специальные шрифты */
        td span[style*="Apple Color Emoji"] {
          font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji', 'EmojiOne Color', 'Android Emoji', 'Twemoji Mozilla', sans-serif !important;
        }
        /* Текст с цифрами в именах пользователей */
        td span:not([style*="Apple Color Emoji"]) {
          font-family: 'Arial', 'Helvetica Neue', Helvetica, sans-serif !important;
          font-variant-numeric: normal !important;
          font-feature-settings: normal !important;
          font-variant-ligatures: normal !important;
          font-kerning: normal !important;
          letter-spacing: normal !important;
        }
      `}</style>
      <h1
        className="text-6xl mb-2 text-gray-900 text-center"
        style={{
          fontFamily: 'Arial Black, Arial, sans-serif',
          fontStyle: 'italic',
          fontWeight: '900'
        }}
      >
        TENNIS TOUR MOLDOVA
      </h1>
      <h2 
        className="text-4xl mb-4 text-gray-800 font-bold text-center"
        style={{
          fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
          fontVariantNumeric: 'normal',
          fontFeatureSettings: 'normal',
          fontVariantLigatures: 'normal',
          fontKerning: 'normal',
          letterSpacing: 'normal',
          lineHeight: '1.2',
          textRendering: 'auto',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale'
        }}
      >
        {reportData.contest?.name || reportData.contestName || 'Отчет конкурса'}
      </h2>
      
      <div className="overflow-x-auto" style={{ padding: '0' }}>
        <table
          className="divide-y divide-gray-200 border border-gray-300"
          style={{
            tableLayout: 'fixed',
            width: `${USER_COL_WIDTH + (reportData.polls.length * POLL_COL_WIDTH) + TOTAL_COL_WIDTH}px`,
            margin: '0 auto'
          }}
        >
          <colgroup>
            <col style={{ width: `${USER_COL_WIDTH}px` }} />
            {reportData.polls.map((poll: any) => (
              <col key={`col-${poll.id}`} style={{ width: `${POLL_COL_WIDTH}px` }} />
            ))}
            <col style={{ width: `${TOTAL_COL_WIDTH}px` }} />
          </colgroup>
          <thead className="bg-gray-50 sticky top-0">
            <tr style={{ height: '150px' }}>
              <th
                className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 sticky left-0 bg-gray-50 z-10"
                style={{ width: `${USER_COL_WIDTH}px`, padding: '2px' }}
              >
                Utilizatori
              </th>
              {reportData.polls.map((poll: any) => (
                <th
                  key={poll.id}
                  className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300"
                  style={{ width: `${POLL_COL_WIDTH}px`, padding: '2px' }}
                >
                  <div className="flex items-center justify-center h-full">
                    <div
                      style={{
                        writingMode: 'vertical-rl',
                        textOrientation: 'mixed',
                        transform: 'rotate(180deg)'
                      }}
                    >
                      <div className="font-bold">{poll.question}</div>
                      {poll.correctOptionText && (
                        <div className="text-xs text-green-600 font-semibold mt-2">
                          {poll.correctOptionText}
                        </div>
                      )}
                    </div>
                  </div>
                </th>
              ))}
              <th
                className="text-center text-xs font-bold text-gray-500 uppercase tracking-wider bg-yellow-50"
                style={{ width: `${TOTAL_COL_WIDTH}px`, padding: '2px' }}
              >
                <div className="flex items-center justify-center h-full">
                  <div
                    style={{
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                      transform: 'rotate(180deg)'
                    }}
                  >
                    TOTAL
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {reportData.users
              .sort((a: any, b: any) => {
                // 1. Сначала по общему количеству очков (по убыванию)
                if (b.totalPoints !== a.totalPoints) {
                  return b.totalPoints - a.totalPoints;
                }

                // 2. Если очки одинаковые, сравниваем по порядку голосований (слева направо)
                for (let i = 0; i < Math.max(a.pollResults.length, b.pollResults.length); i++) {
                  const aPoints = a.pollResults[i]?.points || 0;
                  const bPoints = b.pollResults[i]?.points || 0;
                  if (bPoints !== aPoints) {
                    return bPoints - aPoints;
                  }
                }

                // 3. Если все одинаково, сортируем по алфавиту
                return a.name.localeCompare(b.name);
              })
              .map((user: any, userIndex: number) => (
                <tr
                  key={user.id}
                  className={userIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                  style={{ height: '20px' }}
                >
                  <td
                    className="text-sm text-left font-medium text-gray-900 border-r border-gray-300 sticky left-0 bg-inherit z-10"
                    style={{ padding: '2px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {user.type === 'player' ? (
                        <>
                          <span 
                            className="text-blue-600"
                            style={{ 
                              fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji, EmojiOne Color, Android Emoji, Twemoji Mozilla, sans-serif',
                              fontSize: '1em',
                              lineHeight: '1'
                            }}
                          >
                            👤
                          </span>
                          <span
                            className="text-blue-600"
                            style={{
                              fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
                              fontVariantNumeric: 'normal',
                              fontFeatureSettings: 'normal',
                              fontVariantLigatures: 'normal',
                              fontKerning: 'normal',
                              letterSpacing: 'normal'
                            }}
                          >
                            {user.name}
                          </span>
                        </>
                      ) : (
                        <>
                          <span 
                            className="text-gray-600"
                            style={{ 
                              fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji, EmojiOne Color, Android Emoji, Twemoji Mozilla, sans-serif',
                              fontSize: '1em',
                              lineHeight: '1'
                            }}
                          >
                            📱
                          </span>
                          <span
                            className="text-gray-600"
                            style={{
                              fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
                              fontVariantNumeric: 'normal',
                              fontFeatureSettings: 'normal',
                              fontVariantLigatures: 'normal',
                              fontKerning: 'normal',
                              letterSpacing: 'normal'
                            }}
                          >
                            {user.name}
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  {user.pollResults.map((result: any, pollIndex: number) => (
                    <td
                      key={pollIndex}
                      className="text-sm text-center border-r border-gray-300"
                      style={{ padding: '0' }}
                    >
                      {result ? (
                        result.points > 0 ? (
                          <div className="text-yellow-600 font-bold text-sm" style={{ padding: '0', margin: '0', lineHeight: 1, whiteSpace: 'nowrap' }}>
                            {result.points}
                          </div>
                        ) : (
                          <div className="text-green-600 text-2xl">✓</div>
                        )
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  ))}
                  <td
                    className="text-center font-bold text-yellow-600 bg-yellow-50"
                    style={{
                      width: `${TOTAL_COL_WIDTH}px`,
                      minWidth: `${TOTAL_COL_WIDTH}px`,
                      maxWidth: `${TOTAL_COL_WIDTH}px`,
                      padding: '2px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <div className="text-sm" style={{ lineHeight: 1 }}>
                      {user.totalPoints}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContestReport;

